import { useCallback, type ErrorInfo, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { ErrorBoundary } from '~/components/ErrorBoundary';
import { useDebugModeStore } from '~/scenes/Build/scenes/FlowBuilder/components/Design/state/debugModeStore';
import type { CanvasEntityType } from '~/scenes/Build/scenes/FlowBuilder/errorReporting';
import { useDesignUIStore } from '~/scenes/Build/scenes/FlowBuilder/stores/designUIStore';

type CanvasErrorBoundaryProps = {
  entityType: CanvasEntityType;
  children: ReactNode;
};

type AuthSliceState = {
  authentication?: { userData?: { email?: string } };
};

/**
 * CanvasErrorBoundary — wraps a Canvas (pipeline/capsule) subtree: shows the
 * friendly fallback and reports render errors with pipeline + component context.
 *
 * @example
 * <CanvasErrorBoundary entityType='pipeline'><PipelineDesign /></CanvasErrorBoundary>
 */
export function CanvasErrorBoundary({
  entityType,
  children
}: CanvasErrorBoundaryProps) {
  const { realm = '', pipelineId, capsuleId } = useParams();
  const userEmail = useSelector(
    (state: AuthSliceState) => state.authentication?.userData?.email ?? ''
  );

  const entityId = (entityType === 'capsule' ? capsuleId : pipelineId) ?? '';

  const handleError = useCallback(
    (error: Error, errorInfo: ErrorInfo) => {
      const { activeSidePanel, isReadOnly } = useDesignUIStore.getState();
      const { isDebugMode } = useDebugModeStore.getState();
      const uiState = {
        activeSidePanel: activeSidePanel || null,
        isReadOnly,
        isDebugMode
      };

      import('~/scenes/Build/scenes/FlowBuilder/errorReporting')
        .then(({ reportCanvasError }) =>
          reportCanvasError(
            error,
            errorInfo,
            { realm, entityId, entityType, userEmail },
            uiState
          )
        )
        .catch(() => {});
    },
    [realm, entityId, entityType, userEmail]
  );

  return (
    <ErrorBoundary onError={handleError} resetKeys={[entityId]}>
      {children}
    </ErrorBoundary>
  );
}
